'use client';

import Link from 'next/link';
import { useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { Signin } from '@/actions';
import { GoogleIcon } from '@/assets';
import { Alert, AlertTitle, Button, Divider, Form, FormInputField } from '@/components';
import { authClient } from '@/lib';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { loginFormSchema } from '@/zod-schema';
import { AlertCircleIcon } from 'lucide-react';
import { FaMicrosoft } from 'react-icons/fa';
import RedirectSkeleton from './_components/redirect-skelton';

/**
 * @file login-form.jsx
 * @description Login form
 * @version 0.0.1
 * Created at: 22/09/2025
 * Updated at: 29/10/2025
 */
export const LoginForm = ({ className }) => {
  //-------------- State & Variables --------------//
  const [loadingType, setLoadingType] = useState(null); // "submit" | "google" | null
  const [error, setError] = useState(null);
  const [isReloading, setIsReloading] = useState(false);
  const reloadPendingRef = useRef(false);

  const form = useForm({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const { refetch } = authClient.useSession();

  //-------------- Use Effects --------------//

  //-------------- Other Methods --------------//

  /**
   * Handle Google Sign-In
   */
  const handleGoogleSignIn = async () => {
    try {
      setLoadingType('google');
      const res = await authClient.signIn.social({
        provider: 'google',
        callbackURL: '',
      });

      if (res && res.data) {
        refetch();
      } else {
        setError('Google Sign-In failed');
        logger.warn('Google Sign-In failed', { context: 'LoginForm' });
      }
    } catch (err) {
      logger.error('Google Sign-In failed unexpectedly', err, { context: 'LoginForm' });
      setError('Something went wrong. Please try again later.');
    } finally {
      setLoadingType(null);
    }
  };

  /**
   * Handle form submission
   */
  const onSubmit = async (values) => {
    try {
      setLoadingType('submit');
      const res = await Signin(values);
      logger.info('User logged in successfully', { context: 'LoginForm', user: res });
      if (res && res.user) {
        reloadPendingRef.current = true;
        setIsReloading(true);
        refetch();
        window.location.reload();
        logger.debug('User logged in successfully', { context: 'LoginForm' });
      } else {
        logger.warn('Invalid credentials attempt', { context: 'LoginForm' });
        setError('Invalid email or password');
      }
    } catch (err) {
      logger.error('Login failed unexpectedly', err, { context: 'LoginForm' });
      if (reloadPendingRef.current) {
        reloadPendingRef.current = false;
        setIsReloading(false);
      }
      setError('Something went wrong. Please try again later.');
    } finally {
      if (!reloadPendingRef.current) {
        setLoadingType(null);
      }
    }
  };

  /**
 * Handle Microsoft Sign-In
 */
  const handleMicrosoftSignIn = async () => {
    try {
      setLoadingType('microsoft');
      const res = await authClient.signIn.social({
        provider: 'microsoft',
        callbackURL: '',
      });

      if (res && res.data) {
        refetch();
      } else {
        setError(res?.message || 'Microsoft Sign-In failed');
      }
    } catch (err) {
      console.log('Error in MicrosoftSignIn', err);
      setError(err?.message || 'Microsoft Sign-In failed');
    } finally {
      setLoadingType(null);
    }
  };

  const isSubmitting = loadingType !== null || isReloading;

  return (
    <div className="relative">
      {isReloading &&
        <div className="fixed inset-0 z-50" >
          <RedirectSkeleton />
        </div>}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className={cn('mt-4 space-y-8', className)}>
          <fieldset disabled={isSubmitting} className="space-y-8">
            <FormInputField
              form={form}
              name={'email'}
              label="Email"
              placeholder="email@gmail.com"
            />
            <div>
              <FormInputField
                form={form}
                name={'password'}
                type="password"
                label="Password"
                placeholder="*******"
              />
              <Link
                href="/forgot-password"
                className="mt-1 mr-0 ml-auto block w-fit text-sm underline-offset-4 hover:underline"
              >
                Forgot your password?
              </Link>
            </div>
            {error && !isSubmitting && (
              <Alert variant="destructive">
                <AlertCircleIcon />
                <AlertTitle>{error}</AlertTitle>
              </Alert>
            )}

            <Button
              type="submit"
              className={'w-full'}
              isLoading={loadingType === 'submit' || isReloading}
            >
              Submit
            </Button>
          </fieldset>
        </form>
      </Form>

      <Divider className={'mt-6'}>Or continue with</Divider>
      <Button
        variant={'outline'}
        className={'mt-5 w-full'}
        isLoading={loadingType === 'google'}
        onClick={handleGoogleSignIn}
        disabled={isSubmitting}
      >
        <GoogleIcon />
        Login with Google
      </Button>
      <Button
        variant={'outline'}
        className={'mt-5 w-full'}
        isLoading={loadingType === 'microsoft'}
        onClick={handleMicrosoftSignIn}
        disabled={isSubmitting}
      >
        <FaMicrosoft />
        Login with Microsoft
      </Button>
    </div>
  );
};
